import React from 'react';

import {
  Timer,
  formatSeconds,
  TimerHandlers,
  useTimerContext
} from '../contexts/timer';

const EMPTY: Timer[] = [];
const NO_HANDLERS: TimerHandlers = {};

/** The whole list plus actions. Re-renders only on add, remove or status changes. */
export const useTimers = () => {
  const store = useTimerContext();
  const timers = React.useSyncExternalStore(
    store.subscribe,
    store.getTimers,
    () => EMPTY
  );
  return React.useMemo(() => ({ timers, ...store.actions }), [timers, store]);
};

/** Actions only. Never re-renders, so it's good for buttons or other hooks. */
export const useTimerActions = () => {
  return useTimerContext().actions;
};

/** Registers onStart / onStop / onElapsed for one timer while the calling component is mounted. */
export const useTimerEvents = (id: string, handlers: TimerHandlers) => {
  const store = useTimerContext();

  // Ref keeps the registration stable while always calling your latest functions,
  // so inline arrow functions don't cause re-registering.
  const ref = React.useRef(handlers);
  React.useEffect(() => {
    ref.current = handlers;
  });

  React.useEffect(
    () => store.registerHandlers(id, () => ref.current),
    [store, id]
  );
};

/** One timer: state, formatted time, bound actions. Re-renders at most once per displayed second. */
export const useTimer = (id: string, handlers: TimerHandlers = NO_HANDLERS) => {
  const store = useTimerContext();
  useTimerEvents(id, handlers);

  const timer = React.useSyncExternalStore(
    store.subscribe,
    () => store.getTimer(id),
    () => undefined
  );
  // Primitive snapshot, so a tick that doesn't change the shown second causes no re-render.
  const seconds = React.useSyncExternalStore(
    store.subscribe,
    () => store.getSeconds(id),
    () => 0
  );
  const actions = React.useMemo(
    () => ({
      start: () => store.actions.start(id),
      pause: () => store.actions.pause(id),
      stop: () => store.actions.stop(id),
      remove: () => store.actions.remove(id)
    }),
    [store, id]
  );

  return { timer, seconds, formatted: formatSeconds(seconds), ...actions };
};

/** Just the displayed seconds, for a lightweight display component. */
export const useTimerSeconds = (id: string) => {
  const store = useTimerContext();
  return React.useSyncExternalStore(
    store.subscribe,
    () => store.getSeconds(id),
    () => 0
  );
};
